import Utils from 'core-helpers/utils.js';
import FlagInteractor from 'components/flag-interactor-class.js';
import ItemRotator from 'components/item-rotator-class.js';
import ItemChildHeightCalculator from 'components/item-child-height-calculator-class.js';
import FlagsCarouselFactory from 'components/flags-carousel/flags-carousel-factory';
import TranslationsService from 'common/translations.service.ts';
import {initBGHeader} from 'components/main-header-wave-background.js';
import Tracking from 'tracking/tracking-controller.ts';
import {DYNAMIC_FLAGS_READY_EVENT} from 'components/flags-list-constants.js';
import PubSub from 'pubsub-js/src/pubsub.js';

const HEADER_ROTATION_INTERVAL = 3000;

const onMouseEnterFlag = function (headerCta, value) {
    const lang = TranslationsService.getTranslation(`LANGUAGE_${value}`);
    Utils.setText(headerCta, TranslationsService.getAndReplaceTranslation('START_LEARNING', lang));
};

const onMouseLeaveFlag = function (headerCta) {
    Utils.setText(headerCta, TranslationsService.getTranslation('START_LEARNING_NOW'));
};

const initNavLinksTracking = () => {
    const NAV_LINKS = {
        nav_link_howItWorks: [Utils.getById('nav-link-how-works'), Utils.getById('nav-link-how-works-dropdown')],
        nav_link_reviews: [Utils.getById('nav-link-reviews'), Utils.getById('nav-link-reviews-dropdown')],
        nav_link_liveLessons: [Utils.getById('nav-link-live-lessons'), Utils.getById('nav-link-live-lessons-dropdown')],
        nav_link_plans: [Utils.getById('nav-link-plans'), Utils.getById('nav-link-plans-dropdown')],
    };

    const DESTINATIONS = {
        nav_link_howItWorks: 'how-it-works',
        nav_link_reviews: 'app-trust',
    };

    const clickAction = (key) => {
        if (NAV_LINKS[key]) {
            const trackingData = {
                type: `${key}`,
                page: window.location.href,
            };

            const destination = DESTINATIONS[key];

            const trackEvent = (element) => {
                if (element) {
                    element.addEventListener('click', (e) => {
                        const scrollDestination = Utils.getById(destination);
                        if (scrollDestination) {
                            e.preventDefault();
                            scrollDestination.scrollIntoView({
                                behavior: 'smooth',
                            });
                        }
                        Tracking.sendAndWait('HOME_NAV_LINKS_CLICKED', trackingData);
                    });
                }
            };

            NAV_LINKS[key].forEach(trackEvent);
        }
    };

    Object.keys(NAV_LINKS).forEach(clickAction);
};

const initSignupsCTATracking = () => {
    const SIGNUP_CTAS = {
        nav_signup_cta: Utils.getById('nav-signup-cta'),
        header_signup_cta: Utils.getById('main-header-cta'),
        awards_signup_cta: Utils.getById('app-awards-cta'),
    };

    const clickAction = (key) => {
        if (SIGNUP_CTAS[key]) {
            const trackingData = {
                type: `${key}`,
                page: window.location.href,
            };
            SIGNUP_CTAS[key].addEventListener('click', () =>
                Tracking.sendAndWait('HOME_SIGNUPS_CTA_CLICKED', trackingData)
            );
        }
    };

    Object.keys(SIGNUP_CTAS).forEach(clickAction);
};

const initFlags = () => {
    const flagWrapper = Utils.getElementByClass('choose-language-scrollable');
    const headerCta = Utils.getById('main-header-cta');

    if (flagWrapper && headerCta) {
        new FlagInteractor(flagWrapper, {
            flagClass: 'js-flag',
            onMouseEnter: onMouseEnterFlag.bind(null, headerCta),
            onMouseLeave: onMouseLeaveFlag.bind(null, headerCta),
        });
    }
};

const initTitleTextRotator = (wrapperClass) => {
    const titlesWrapper = Utils.getElementByClass(wrapperClass);

    if (titlesWrapper) {
        new ItemChildHeightCalculator(titlesWrapper, {
            calculateClass: 'main-header-wave__title-rotator-item',
        });

        new ItemRotator(titlesWrapper, {
            interval: HEADER_ROTATION_INTERVAL,
            initializedClass: 'main-header-wave__title-rotator--initialized',
            itemPrevClass: 'main-header-wave__title-rotator-item--prev',
            itemActiveClass: 'main-header-wave__title-rotator-item--active',
            itemNextClass: 'main-header-wave__title-rotator-item--next',
        });
    }
};

const initFlagsCarousel = () => {
    const container = Utils.getElementByClass('choose-language-scrollable__flags-container');
    const content = Utils.getElementByClass('choose-language-scrollable__flags-container-content');

    const shouldScroll = () => content && container && content.scroll && content.scrollWidth > container.clientWidth;

    const initCarousel = () => {
        const carouselWrapper = Utils.getElementByClass('choose-language-scrollable__carousel');

        FlagsCarouselFactory(carouselWrapper, {
            container,
            content,
            leftArrow: Utils.getElementByClass('choose-language-scrollable__carousel-arrow--left'),
            rightArrow: Utils.getElementByClass('choose-language-scrollable__carousel-arrow--right'),
        });
    };

    // Init a static flag list carousel ?
    if (shouldScroll()) {
        initCarousel();
        return;
    }

    // Init a dynamic flag list carousel ?
    PubSub.subscribe(DYNAMIC_FLAGS_READY_EVENT, function () {
        if (shouldScroll()) {
            initCarousel();
        }
    });
};

const init = () => {
    const carouselWrapper = Utils.getById('js-language-scrollable');

    initSignupsCTATracking();
    initNavLinksTracking();
    initFlags();
    if (carouselWrapper) {
        initFlagsCarousel();
    }
    initBGHeader()
        .catch(() => {})
        .then(initTitleTextRotator.bind(null, 'js-title-rotator'))
        .then(initTitleTextRotator.bind(null, 'js-subtitle-rotator'));

    /**
     * LIVE_GROUP_NAV_LINK experiment
     * - 50% of EN/DE Homepage users see the Live Group link
     * - 50% of EN/DE Homepage users do not see the Live Group link
     */
    const MAIN_NAV_CLASS = 'nav-dropdown';
    const LIVE_LINK_JS_CLASS = 'js-live-group-link';
    const GENERIC_HIDDEN_CLASS = 'hidden';
    const mainNavElement = Utils.getElementByClass(MAIN_NAV_CLASS);

    if (mainNavElement) {
        const liveGroupLinkElements = Utils.getElementsByClass(LIVE_LINK_JS_CLASS, mainNavElement);
        if (liveGroupLinkElements && liveGroupLinkElements.length) {
            liveGroupLinkElements.forEach((link) => {
                Utils.removeClass(link, GENERIC_HIDDEN_CLASS);
                link.addEventListener('click', () => Tracking.sendAndWait('NAVBAR_GROUP_LINK_SELECTED'));
            });
        }
    }
};

init();

export {init};
